import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FlightStepper from "@/components/site/FlightStepper";
import FareRulesDialog from "@/components/site/FareRulesDialog";
import { draftStore, computeTotal } from "@/lib/flightDraft";
import { formatCheckedBaggage } from "@/lib/bagFormat";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { toast } from "sonner";
import {
  ArrowLeft, Plane, Clock, ArrowRight, User, ShieldCheck,
  Briefcase, Utensils, Armchair, Lock, X, Tag, FileText
} from "lucide-react";

function fmtDur(min) { const h = Math.floor(min / 60), m = min % 60; return `${h}h ${m}m`; }

export default function FlightReview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { format: fmt, currency: dispCcy } = useCurrency();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [activePromos, setActivePromos] = useState([]);
  const [fareRulesFor, setFareRulesFor] = useState(null);  // {idx, flight} | null
  // Live price-drift state: { checking, drift_pct, live_total_usd, displayed_total_usd, acknowledged }
  const [priceDrift, setPriceDrift] = useState({ checking: false, drift_pct: 0, live_total_usd: 0, displayed_total_usd: 0, acknowledged: false });

  // Fetch the list of public promo codes to surface below the input
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/promos/active");
        if (alive) setActivePromos(Array.isArray(data) ? data : []);
      } catch (_e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    const d = draftStore.get();
    if (!d || !d.selections?.length || !d.travelers?.length) { navigate("/flights"); return; }
    setDraft(d);
  }, [user, navigate]);

  // ── Price-stability re-check ────────────────────────────────────────────────
  // For every Travelport segment, ping AirPriceReq on page load. Sum the live
  // USD totals across segments and compare against the air subtotal we show in
  // the breakdown. If they differ by more than 1% surface an amber chip so the
  // customer can re-confirm before paying.
  useEffect(() => {
    if (!draft) return;
    const tpSels = (draft.selections || []).filter(
      (s) => (s.flight.supplier_source || s.flight.source) === "travelport"
    );
    if (tpSels.length === 0) return;
    let alive = true;
    setPriceDrift((s) => ({ ...s, checking: true }));
    (async () => {
      let liveUsd = 0; let ok = true;
      const paxN = Number(draft.search?.passengers || 1);
      for (const sel of tpSels) {
        try {
          const { data } = await api.post("/travelport/availability-check", {
            flight: sel.flight, pax_count: paxN,
          });
          if (data?.ok === false) { ok = false; break; }
          const segLive = Number(data?.live_total_usd) || 0;
          liveUsd += segLive;
        } catch (_e) { /* network glitch — surface nothing, don't block */ }
      }
      if (!alive) return;
      // Compare live air subtotal against draft's air subtotal (base+taxes for
      // selected brand × pax, NO ancillaries — AirPrice only re-quotes the air
      // portion). Drift = |live − displayed| / displayed.
      const displayedAirSubtotal = (() => {
        // Use the per-pax air price × pax (same definition as in subTotalAir above).
        let total = 0;
        for (const sel of tpSels) {
          total += (Number(sel.flight.price || 0) + Number(sel.fare?.price_delta || 0)) * paxN;
        }
        return Math.round(total * 100) / 100;
      })();
      if (!ok || displayedAirSubtotal <= 0 || liveUsd <= 0) {
        setPriceDrift({ checking: false, drift_pct: 0, live_total_usd: liveUsd,
                        displayed_total_usd: displayedAirSubtotal, acknowledged: true });
        return;
      }
      const driftPct = Math.abs(liveUsd - displayedAirSubtotal) / displayedAirSubtotal * 100;
      setPriceDrift({
        checking: false,
        drift_pct: Math.round(driftPct * 100) / 100,
        live_total_usd: Math.round(liveUsd * 100) / 100,
        displayed_total_usd: displayedAirSubtotal,
        acknowledged: driftPct <= 1.0,  // auto-acknowledged when stable
      });
    })();
    return () => { alive = false; };
  }, [draft]);

  if (!draft) return null;

  const pax = Number(draft.search?.passengers || 1);
  const anc = draft.ancillaries || {};

  const seatLines = Object.entries(anc.seats || {});
  const bagLines = Object.entries(anc.bags || {});
  const mealLines = Object.entries(anc.meals || {});

  // Per-pax base + taxes (incl. brand fare delta — both base & taxes scale with delta proportionally)
  const segPriceParts = draft.selections.map((sel) => {
    const f = sel.flight, bf = sel.fare;
    const farePrice = Number(f.price || 0) + Number(bf.price_delta || 0);
    const base = Number(bf.base_price ?? f.base_price ?? 0);
    const taxes = Number(bf.taxes ?? f.taxes ?? 0);
    const knownSum = base + taxes;
    // If brand fare didn't expose its own base/tax split, scale the search-offer split
    if (Math.abs(knownSum - farePrice) > 0.5 && knownSum > 0) {
      const scale = farePrice / knownSum;
      return { base: base * scale, taxes: taxes * scale };
    }
    // For upsell fares where price_delta was added on top of original price, distribute proportionally
    if (knownSum < farePrice && knownSum > 0) {
      const scale = farePrice / knownSum;
      return { base: base * scale, taxes: taxes * scale };
    }
    return { base, taxes };
  });
  const rawBaseSum = segPriceParts.reduce((s, p) => s + p.base, 0) * pax;
  const rawTaxSum = segPriceParts.reduce((s, p) => s + p.taxes, 0) * pax;
  const rawSubTotalAir = rawBaseSum + rawTaxSum;

  // ── Live-price override ──────────────────────────────────────────────────
  // When the airline's live AirPrice quote diverged >1% and the user clicked
  // "Accept new price", we MUST honour that new number for the booking total.
  // Scale base/taxes proportionally so the breakdown still ties to the new
  // subtotal AND keeps a reasonable base/tax split for downstream invoicing.
  const liveOverrideActive = priceDrift.drift_pct > 1.0 && priceDrift.acknowledged && priceDrift.live_total_usd > 0;
  const subTotalAir = liveOverrideActive
    ? Math.round(priceDrift.live_total_usd * 100) / 100
    : rawSubTotalAir;
  const airScale = rawSubTotalAir > 0 ? subTotalAir / rawSubTotalAir : 1;
  const baseSum = Math.round(rawBaseSum * airScale * 100) / 100;
  const taxSum = Math.round(rawTaxSum * airScale * 100) / 100;

  const seatSum = seatLines.reduce((s, [, v]) => s + (v.price || 0), 0);
  const bagSum = bagLines.reduce((s, [, v]) => s + (v.totalPrice || 0), 0);
  const mealSum = mealLines.reduce((s, [, v]) => s + (v.totalPrice || 0), 0);
  const insSum = anc.insurance ? (anc.insurance.price || 0) * pax : 0;
  // Compute grand from the (possibly overridden) air subtotal + add-ons so it
  // ALWAYS reflects what the customer sees. computeTotal(draft) was leaking
  // the stale pre-AirPrice number — that was Bug A.
  const grand = Math.round((subTotalAir + seatSum + bagSum + mealSum + insSum) * 100) / 100;
  // Compute the displayed discount from promo metadata + the *current* baseSum
  // so it auto-re-tracks when the user accepts a new live price (or toggles
  // ancillaries that change the base). The backend re-derives the same number
  // from `base_amount` we send on /bookings → single source of truth.
  const discount = (() => {
    if (!promo) return 0;
    if (promo.discount_type === "percent") {
      const raw = baseSum * (Number(promo.amount) || 0) / 100;
      return Math.min(Math.round(raw * 100) / 100, grand);
    }
    return Math.min(Math.round((Number(promo.amount) || 0) * 100) / 100, grand);
  })();
  const finalTotal = Math.max(0, Math.round((grand - discount) * 100) / 100);

  const applyPromo = async () => {
    if (!promoCode) return;
    setPromoBusy(true);
    try {
      // Apply discount on BASE FARE only — taxes & ancillaries are protected.
      // Backend echoes the discount it computed; we surface that number end-to-end
      // (Review → /bookings → backoffice → reports) so there's a single truth.
      const { data } = await api.post("/promo/validate", {
        code: promoCode,
        amount: grand,
        base_amount: baseSum,
      });
      setPromo(data); toast.success(`Promo applied on base fare: −${fmt(data.discount)}`);
    } catch (e) { setPromo(null); toast.error(e?.response?.data?.detail || "Invalid promo"); }
    finally { setPromoBusy(false); }
  };

  const confirmAndPay = async () => {
    setBusy(true);
    try {
      // ── Pre-payment availability re-check ──────────────────────────────────
      // For every Travelport-supplier segment, ping AirPriceReq to confirm the
      // booked class is still live BEFORE we hand off to Stripe. Catches the
      // "paid but ticket fails with *0 AVAIL/WL CLOSED*" failure mode.
      const tpSegments = draft.selections.filter(
        (sel) => (sel.flight.supplier_source || sel.flight.source) === "travelport"
      );
      for (const sel of tpSegments) {
        try {
          const { data: chk } = await api.post("/travelport/availability-check", {
            flight: sel.flight,
            pax_count: Number(draft.search?.passengers || 1),
          });
          if (chk && chk.ok === false) {
            const segErr = (chk.segment_errors || [])[0];
            const detail = segErr
              ? `${segErr.carrier || ""}${segErr.flight_number || ""} class ${segErr.class || "?"} is no longer available — ${segErr.message || chk.reason}`
              : (chk.reason || "Flight is no longer available. Please pick another one.");
            toast.error(detail, { duration: 8000 });
            setBusy(false);
            return;  // ABORT — never charge the card
          }
        } catch (chkErr) {
          // Availability re-check itself failed → fail closed: don't proceed.
          toast.error("Could not verify availability — please retry in a moment.");
          setBusy(false);
          return;
        }
      }

      // Build full flight item snapshot for the booking
      const item = {
        trip_type: draft.search.trip_type,
        passengers: pax,
        cabin: draft.search.cabin,
        segments: draft.selections.map((sel) => ({
          airline_code: sel.flight.airline_code,
          airline_name: sel.flight.airline_name,
          flight_number: sel.flight.flight_number,
          aircraft: sel.flight.aircraft,
          from: sel.flight.from,
          to: sel.flight.to,
          date: sel.flight.date,
          depart_time: sel.flight.depart_time,
          arrive_time: sel.flight.arrive_time,
          duration_min: sel.flight.duration_min,
          stops: sel.flight.stops,
          base_price: sel.flight.base_price,
          taxes: sel.flight.taxes,
          markup_applied: sel.flight.markup_applied || 0,
          source: sel.flight.source,
          source_label: sel.flight.source_label,
          supplier_source: sel.flight.supplier_source || sel.flight.source,
          ndcx_offer_id: sel.flight.ndcx_offer_id,
          ndcx_owner_code: sel.flight.ndcx_owner_code,
          ndcx_shopping_response_id: sel.flight.ndcx_shopping_response_id,
          travelport_segments: sel.flight.travelport_segments,
          travelport_price_point_key: sel.flight.travelport_price_point_key,
          plating_carrier: sel.flight.plating_carrier,
          fare_basis: sel.flight.fare_basis,
          rbd: sel.flight.rbd,
          // FX trail — without these the booking record drops `original_currency`,
          // `original_total_amount`, `fx_rate_applied`, etc., and admin reports
          // show "—" for every FX column even for clearly converted bookings.
          fx: sel.flight.fx,
          native_price: sel.flight.native_price,
          native_currency: sel.flight.native_currency,
          original_currency: sel.flight.original_currency,
          original_price: sel.flight.original_price,
          // Amadeus offer payload — required by the Stripe webhook and
          // /admin/bookings/.../retry-issuance to re-price and call
          // /v1/booking/flight-orders. WITHOUT this, ticket issuance has
          // nothing to send and the booking sits as paid-without-ticket
          // forever. Prefer the upsold offer if the customer chose one,
          // else fall back to the search offer.
          amadeus_offer: sel.fare?._upsell_offer || sel.flight.amadeus_offer,
          fare: sel.fare,
        })),
        ancillaries: anc,
        fare_breakdown: {
          air_subtotal: subTotalAir,
          seats: seatSum,
          extra_baggage: bagSum,
          meals: mealSum,
          insurance: insSum,
        },
      };
      const { data } = await api.post("/bookings", {
        booking_type: "flight", item,
        travelers: draft.travelers,
        total_amount: grand, currency: "USD",
        // Base fare passed explicitly so the backend applies %-promos on
        // BASE only (taxes & ancillaries stay full-price). Without this, the
        // backend was applying 10% to the whole total → $22 instead of $13.38.
        base_amount: baseSum,
        // Air taxes — persisted so Custom Reports can show a truthful
        // Tax Amount column instead of summing the per-segment stale split.
        tax_amount: taxSum,
        contact: draft.contact,
        promo_code: promo ? promo.code : undefined,
      });
      // Move forward to checkout (which then redirects to Stripe)
      draftStore.clear();
      navigate(`/checkout/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create booking");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] rounded-3xl p-6 md:p-8 flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-white font-heading text-2xl sm:text-3xl font-semibold">Review your booking</h1>
        <FlightStepper current="review" />
      </div>

      {/* ── Live price-drift chip ─────────────────────────────────────────── */}
      {priceDrift.checking && (
        <div data-testid="price-drift-checking" className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-[12px] text-slate-500">
          <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-pulse" /> Verifying live fare with the airline…
        </div>
      )}
      {!priceDrift.checking && priceDrift.drift_pct > 1.0 && !priceDrift.acknowledged && (
        <div data-testid="price-drift-warning" className="mt-4 flex items-start gap-3 p-3 rounded-2xl bg-amber-50 border border-amber-300">
          <div className="w-9 h-9 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center flex-shrink-0">
            <i className="fa-solid fa-triangle-exclamation text-base" aria-hidden="true"></i>
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">Live price changed — confirm to proceed</div>
            <div className="text-xs text-amber-800 mt-0.5">
              The airline now quotes <span className="font-mono font-semibold">{fmt(priceDrift.live_total_usd)}</span> for your air fare, vs <span className="font-mono font-semibold">{fmt(priceDrift.displayed_total_usd)}</span> shown ({priceDrift.drift_pct.toFixed(1)}% drift). Ancillaries are unaffected.
            </div>
          </div>
          <Button
            data-testid="price-drift-accept"
            size="sm"
            className="rounded-full bg-amber-700 hover:bg-amber-800 text-white"
            onClick={() => setPriceDrift((s) => ({ ...s, acknowledged: true }))}
          >
            Accept new price
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-5">
          {/* Flights */}
          {draft.selections.map((sel, i) => {
            const f = sel.flight, bf = sel.fare;
            return (
              <Card key={i} className="p-5" data-testid={`review-segment-${i}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-teal-50 text-[var(--brand-primary)] flex items-center justify-center"><Plane className="w-5 h-5" /></div>
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wider font-bold">Segment {i + 1}</div>
                      <div className="font-heading font-semibold text-[var(--brand-accent)]">{f.airline_name} · {f.flight_number}</div>
                    </div>
                  </div>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[var(--brand-secondary)] text-[#064e3b] font-bold uppercase tracking-wider">{bf.name} fare</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mt-4 items-center">
                  <div className="md:col-span-2 text-center md:text-left">
                    <div className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">{f.depart_time}</div>
                    <div className="text-xs text-slate-500 font-mono">{f.from} · {f.date}</div>
                  </div>
                  <div className="md:col-span-3 flex flex-col items-center text-xs text-slate-500">
                    <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {fmtDur(f.duration_min)}</div>
                    <div className="w-full border-t border-dashed border-slate-300 my-2" />
                    <div>{f.stops === 0 ? "Non-stop" : `${f.stops} stop${f.stops > 1 ? "s" : ""}`} · {f.aircraft}</div>
                  </div>
                  <div className="md:col-span-2 text-center md:text-right">
                    <div className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">{f.arrive_time}</div>
                    <div className="text-xs text-slate-500 font-mono">{f.to}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4 text-xs">
                  <span className="inline-flex items-center gap-1.5 bg-slate-50 px-2 py-1.5 rounded-lg"><Briefcase className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Cabin {bf.cabin_baggage_kg}kg</span>
                  <span className="inline-flex items-center gap-1.5 bg-slate-50 px-2 py-1.5 rounded-lg"><Briefcase className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Check-in {formatCheckedBaggage(bf.checked_baggage_qty, bf.checked_baggage_kg)}</span>
                  <span className="inline-flex items-center gap-1.5 bg-slate-50 px-2 py-1.5 rounded-lg"><Utensils className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> {bf.meal ? "Meal incl." : "No meal"}</span>
                  <span className="inline-flex items-center gap-1.5 bg-slate-50 px-2 py-1.5 rounded-lg"><Armchair className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> {bf.seat_selection ? "Free seat" : "Paid seat"}</span>
                </div>

                <div className="mt-3 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={() => setFareRulesFor({ idx: i, flight: f })}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--brand-primary)] hover:text-[#0f766e] hover:underline underline-offset-2 transition-colors"
                    data-testid={`view-fare-rules-btn-${i}`}
                  >
                    <FileText className="w-3.5 h-3.5" /> View fare rules
                  </button>
                </div>
              </Card>
            );
          })}

          {/* Travelers */}
          <Card className="p-5" data-testid="review-travelers">
            <div className="flex items-center gap-2"><User className="w-4 h-4 text-[var(--brand-primary)]" /><h2 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Travelers</h2></div>
            <div className="mt-3 space-y-2">
              {draft.travelers.map((t, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 text-sm py-2 border-b border-slate-100 last:border-0">
                  <div className="md:col-span-4 font-medium text-[var(--brand-accent)]">{i + 1}. {t.title} {t.first_name} {t.last_name}</div>
                  <div className="md:col-span-2 text-slate-500">{t.gender} · DOB {t.dob}</div>
                  <div className="md:col-span-3 text-slate-500">{t.nationality}</div>
                  <div className="md:col-span-3 text-slate-500 font-mono text-xs">Passport {t.passport_number} · exp {t.passport_expiry}</div>
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-500 mt-3">
              Contact: <span className="font-medium text-[var(--brand-accent)]">{draft.contact?.name}</span> · {draft.contact?.email} · {draft.contact?.phone}
            </div>
          </Card>

          {/* Add-ons */}
          {(seatLines.length || bagLines.length || mealLines.length || anc.insurance) ? (
            <Card className="p-5" data-testid="review-addons">
              <h2 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Add-ons</h2>
              <div className="mt-3 space-y-2 text-sm">
                {seatLines.length > 0 && (
                  <div>
                    <div className="font-medium text-[var(--brand-accent)] flex items-center gap-1.5"><Armchair className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Seats</div>
                    <ul className="ml-5 list-disc text-slate-600 text-xs mt-1">
                      {seatLines.map(([k, v]) => {
                        const [sIdx, pIdx] = k.split("-").map(Number);
                        const trav = draft.travelers[pIdx];
                        return <li key={k}>Segment {sIdx + 1} · {trav?.first_name} {trav?.last_name}: {v.category} <span className="text-slate-400">(+{fmt(v.price)})</span></li>;
                      })}
                    </ul>
                  </div>
                )}
                {bagLines.length > 0 && (
                  <div>
                    <div className="font-medium text-[var(--brand-accent)] flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Extra baggage</div>
                    <ul className="ml-5 list-disc text-slate-600 text-xs mt-1">
                      {bagLines.map(([k, v]) => {
                        const [sIdx, pIdx] = k.split("-").map(Number);
                        const trav = draft.travelers[pIdx];
                        return <li key={k}>Segment {sIdx + 1} · {trav?.first_name} {trav?.last_name}: {formatCheckedBaggage(v.qty, v.kg)} <span className="text-slate-400">(+{fmt(v.totalPrice)})</span></li>;
                      })}
                    </ul>
                  </div>
                )}
                {mealLines.length > 0 && (
                  <div>
                    <div className="font-medium text-[var(--brand-accent)] flex items-center gap-1.5"><Utensils className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Meals</div>
                    <ul className="ml-5 list-disc text-slate-600 text-xs mt-1">
                      {mealLines.map(([k, v]) => {
                        const [sIdx, pIdx] = k.split("-").map(Number);
                        const trav = draft.travelers[pIdx];
                        return <li key={k}>Segment {sIdx + 1} · {trav?.first_name} {trav?.last_name}: {v.name} <span className="text-slate-400">(+{fmt(v.totalPrice)})</span></li>;
                      })}
                    </ul>
                  </div>
                )}
                {anc.insurance && (
                  <div className="flex items-center gap-1.5 font-medium text-[var(--brand-accent)]"><ShieldCheck className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> {anc.insurance.name} <span className="text-slate-400 text-xs font-normal">(+{fmt(insSum)} for {pax} pax)</span></div>
                )}
              </div>
            </Card>
          ) : null}
        </div>

        {/* Summary */}
        <Card className="p-5 h-fit sticky top-20" data-testid="review-summary">
          <h3 className="font-heading text-lg font-semibold text-[var(--brand-accent)]">Price summary</h3>
          <div className="mt-3 text-sm space-y-2">
            <div className="flex justify-between items-center text-slate-600">
              <button type="button" onClick={() => setBreakdownOpen(!breakdownOpen)}
                      data-testid="review-breakdown-toggle"
                      className="inline-flex items-center gap-1 text-left hover:text-[var(--brand-primary-dark)]">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-teal-50 text-[var(--brand-primary)] font-bold text-[10px]">
                  {breakdownOpen ? "−" : "+"}
                </span>
                Air fares ({pax} pax)
              </button>
              <span>{fmt(subTotalAir)}</span>
            </div>
            {breakdownOpen && (
              <div data-testid="review-breakdown-content" className="ml-5 space-y-1 bg-slate-50 border border-slate-200 rounded-md p-2 text-xs">
                <div className="flex justify-between text-slate-600"><span>Base fare</span><span className="font-mono">{fmt(baseSum)}</span></div>
                <div className="flex justify-between text-slate-600"><span>Taxes & fees</span><span className="font-mono">{fmt(taxSum)}</span></div>
                <div className="border-t border-slate-200 pt-1 flex justify-between font-semibold text-[var(--brand-accent)]">
                  <span>Air subtotal</span><span className="font-mono">{fmt(subTotalAir)}</span>
                </div>
              </div>
            )}
            {seatSum > 0 && <div className="flex justify-between text-slate-600"><span>Seats</span><span>{fmt(seatSum)}</span></div>}
            {bagSum > 0 && <div className="flex justify-between text-slate-600"><span>Extra baggage</span><span>{fmt(bagSum)}</span></div>}
            {mealSum > 0 && <div className="flex justify-between text-slate-600"><span>Meals</span><span>{fmt(mealSum)}</span></div>}
            {insSum > 0 && <div className="flex justify-between text-slate-600"><span>Insurance</span><span>{fmt(insSum)}</span></div>}
            <div className="border-t border-slate-200 my-2" />
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmt(grand)}</span></div>
            {/* Promo input */}
            <div className="pt-2">
              {promo ? (
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5 text-sm">
                  <span><span className="font-mono font-bold text-emerald-700">{promo.code}</span> applied on base fare</span>
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-700 font-medium">−{fmt(discount)}</span>
                    <button
                      data-testid="review-promo-remove"
                      type="button"
                      onClick={() => { setPromo(null); setPromoCode(""); toast.success("Promo removed"); }}
                      className="p-1 rounded-full hover:bg-emerald-100 text-emerald-700 transition-colors"
                      aria-label="Remove promo code"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <input data-testid="review-promo-input" className="flex-1 h-9 rounded-md border border-slate-200 px-2 text-sm uppercase" placeholder="Promo code" value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} />
                    <Button data-testid="review-promo-apply" size="sm" variant="outline" disabled={promoBusy || !promoCode} onClick={applyPromo}>{promoBusy ? "…" : "Apply"}</Button>
                  </div>
                  {activePromos.length > 0 && (
                    <div data-testid="review-promo-available" className="mt-2">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1">
                        <Tag className="w-2.5 h-2.5" /> Available offers
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {activePromos.map((p) => {
                          const label = p.discount_type === "percent" ? `${p.amount}% off` : `$${p.amount} off`;
                          return (
                            <button
                              key={p.code}
                              type="button"
                              data-testid={`review-promo-chip-${p.code}`}
                              onClick={() => setPromoCode(p.code)}
                              title={p.description || label}
                              className="text-[11px] inline-flex items-center gap-1 bg-teal-50 hover:bg-teal-100 text-teal-700 border border-teal-200 rounded-full px-2 py-0.5 transition-colors"
                            >
                              <span className="font-mono font-bold">{p.code}</span>
                              <span className="text-teal-500">·</span>
                              <span>{label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex justify-between font-heading text-lg font-bold text-[var(--brand-accent)]"><span>Total</span><span>{fmt(finalTotal)} {dispCcy}</span></div>
          </div>
          <Button data-testid="review-confirm-pay-btn"
                  disabled={busy || (priceDrift.drift_pct > 1.0 && !priceDrift.acknowledged)}
                  onClick={confirmAndPay}
                  className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full h-12 mt-5 text-base">
            <Lock className="w-4 h-4 mr-2" /> {busy ? "Preparing..." : (priceDrift.drift_pct > 1.0 && !priceDrift.acknowledged ? "Accept new price first" : "Confirm & Pay")}
          </Button>
          <p className="text-xs text-slate-500 mt-3 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-[var(--brand-primary)]" /> Secured by Stripe · Test mode
          </p>
          <Button type="button" variant="ghost" className="w-full mt-2 rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to add-ons
          </Button>
        </Card>
      </div>

      {fareRulesFor && (
        <FareRulesDialog
          open={true}
          onOpenChange={(o) => { if (!o) setFareRulesFor(null); }}
          flight={fareRulesFor.flight}
          segmentLabel={`Segment ${fareRulesFor.idx + 1} · ${fareRulesFor.flight.from} → ${fareRulesFor.flight.to}`}
        />
      )}
    </div>
  );
}
